import { Dispatch, SetStateAction, useMemo } from 'react';
import { useApplicationStatuses } from '@axo/shared/data-access/hooks';
import { ace_index, loan_application } from '@axo/shared/data-access/types';
import { FilterSet } from '../FilterSet';
import { MultiFilterSelect } from '../MultiFilterSelect';
import { setPageNumber } from '../SearchResults';

interface Props {
  query: ace_index.FindInput;
  setQuery: Dispatch<SetStateAction<ace_index.FindInput>>;
  editable?: boolean;
}

export function ApplicationStatusFilterSet({
  query,
  setQuery,
  editable = false,
}: Props) {
  const statuses = useApplicationStatuses();
  const groups = useMemo(() => mapStatuses(statuses.data), [statuses]);

  return (
    <FilterSet>
      {groups.map((group) => {
        const selectedCodes = statusesInRange(query, group.Code);
        const values = group.children.filter((status) =>
          selectedCodes.includes(status.Code)
        );

        return (
          <MultiFilterSelect<Status>
            key={group.Code}
            items={group.children}
            onSelect={(option) => setQuery(addStatus(option))}
            onRemove={(_, i) => setQuery(removeStatus(values[i]))}
            stringify={(option) => option.key}
            values={values}
            label={`${group.Code} ${group.Description}`}
            disabled={!editable}
          />
        );
      })}
    </FilterSet>
  );
}

function addStatus(status: Status): SetStateAction<ace_index.FindInput> {
  return (query) => {
    const statuses = new Set(query.LoanApplicationStatuses);

    if (statuses.has(status.Code)) {
      statuses.delete(status.Code);
    } else {
      statuses.add(status.Code);
    }

    const next = Array.from(statuses.values());

    return {
      ...setPageNumber(1)(query),
      LoanApplicationStatuses: next.length > 0 ? next : undefined,
    };
  };
}

function removeStatus(status: Status): SetStateAction<ace_index.FindInput> {
  return (query) => {
    if (!query.LoanApplicationStatuses) {
      return query;
    }

    const next = query.LoanApplicationStatuses.filter(
      (code) => code !== status.Code
    );

    return {
      ...setPageNumber(1)(query),
      LoanApplicationStatuses: next.length > 0 ? next : undefined,
    };
  };
}

function statusesInRange(query: ace_index.FindInput, range: number): number[] {
  if (!query.LoanApplicationStatuses) {
    return [];
  }

  return query.LoanApplicationStatuses.filter((code) =>
    statusInRange(code, range)
  );
}

function statusInRange(status: number, range: number): boolean {
  return Math.floor(status / 100) * 100 === range;
}

// mapStatuses parses the status map returned from the api into
// some kind of actually workable object.
function mapStatuses(map?: loan_application.LoanApplicationMap) {
  if (!map) {
    return [];
  }

  // Add key as property to each status
  const statuses = Object.entries(map).map(([status, config]) => ({
    ...config,
    key: status,
  }));

  // Resolve top level status group (100, 200, etc)
  const groups = statuses.filter(
    (status) => status.Code > 0 && status.Code % 100 === 0
  );

  // Add children to each top level group
  return groups.map((group) => ({
    ...group,
    children: statuses.filter((status) =>
      statusInRange(status.Code, group.Code)
    ),
  }));
}

type Status =
  loan_application.LoanApplicationMap[keyof loan_application.LoanApplicationMap] & {
    key: string;
  };
